// FAQ.tsx
import React, { useState, useEffect } from 'react';
import { marked } from 'marked';
import { ChevronDownIcon, ChevronUpIcon, IconLink } from 'src/components/Icons';

type SectionKey =
  | 'canUseFree'
  | 'differencePro'
  | 'betaProgram'
  | 'suggestFeature'
  | 'switchDeck'
  | 'reportBug'
  | 'minAndroid'
  | 'iosWear'
  | 'desktopPlatforms'
  | 'differenceApps'
  | 'remoteConnection'
  | 'rabbits'
  | 'notFreeAnymore';

interface ExpandableSectionProps {
  title: string;
  markdownContent: string;
  isOpen: boolean;
  anchor: SectionKey;
  onToggle: () => void;
}

/* ---------------- refs for scrolling ---------------- */
const sectionRefs = {} as Record<SectionKey, HTMLElement | null>;

const FAQ: React.FC = () => {
  /* ---------------- open / toggle with auto‑scroll ---------------- */
  const [openSection, setOpenSection] = useState<SectionKey | null>(null);

  const openAndScroll = (key: SectionKey) => {
    setOpenSection((prev) => (prev === key ? null : key));
    setTimeout(() => {
      sectionRefs[key]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  };

  /* ---------------- open via hash ---------------- */
  useEffect(() => {
    const hash = window.location.hash.replace('#', '') as SectionKey;
    if (hash) openAndScroll(hash);
  }, []);

  /* ---------------- link copy helper ---------------- */
  const copyAnchor = async (anchor: SectionKey) => {
    const url = `${window.location.origin}${window.location.pathname}#${anchor}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const area = document.createElement('textarea');
      area.value = url;
      area.style.position = 'fixed';
      area.style.left = '-9999px';
      document.body.appendChild(area);
      area.select();
      document.execCommand('copy');
      document.body.removeChild(area);
    }
  };

  /* ---------------- expandable row ---------------- */
  const ExpandableSection: React.FC<ExpandableSectionProps> = ({
    title,
    markdownContent,
    isOpen,
    onToggle,
    anchor,
  }) => (
    <div
      ref={(el) => (sectionRefs[anchor] = el)}
      className="scroll-mt-24 flex flex-col w-full my-2 p-1 md:mx-3 border-b rounded-lg border-gray-600/50"
    >
      <button
        className={`flex justify-between items-center w-full gap-5 text-left rounded-lg p-3 ${
          isOpen ? 'bg-blue-600/50' : ''
        } text-gray-300 hover:bg-blue-600/50`}
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={`${anchor}-content`}
      >
        <h2 className="text-lg">{title}</h2>
        <div className="flex items-center space-x-2">
          <span
            onClick={(e) => {
              e.stopPropagation();
              copyAnchor(anchor);
            }}
            title="Copy link"
            className="text-gray-400 hover:text-gray-200"
          >
            <IconLink className="w-5 h-5" />
          </span>
          {isOpen ? (
            <ChevronUpIcon className="w-5 h-5" />
          ) : (
            <ChevronDownIcon className="w-5 h-5" />
          )}
        </div>
      </button>

      {isOpen && (
        <div
          id={`${anchor}-content`}
          className="rounded-lg bg-bgBorderBlue bg-opacity-30 py-6 px-4 text-gray-300"
          dangerouslySetInnerHTML={{ __html: marked(markdownContent) }}
        />
      )}
    </div>
  );

  /* ---------------- markdown contents ---------------- */
    // Markdown contents
    const contents: { [key in SectionKey]: string } = {
        canUseFree: `
Deck Hopper offers a free 10-day trial. After that, you can upgrade to Basic to continue using it, or choose Pro to remove ads and unlock all advanced features. (Not available on iOS—purchase is required immediately.)
        `,
        differencePro: `
Deck Hopper Basic allows you to use Deck Hopper after the 10-day trial. Deck Hopper Pro removes all ads permanently and adds advanced features. Pro access is valid across all your devices and platforms. (Not available on iOS—Pro purchase is required immediately.)
        `,
        betaProgram: `
The Beta Program is available for [Patrons](https://www.patreon.com/Christinna).
Patrons can test new features before they are released to the public, and suggest and vote on new features.
        `,
        suggestFeature: `
You can suggest a feature or vote on a feature by becoming a [Patron](https://www.patreon.com/Christinna).
        `,
        switchDeck: `
You can switch decks via SAMMI by using [Deck App: Switch Deck](https://sammi.solutions/docs/commands/deck-app#deckappswitchdeck) command in SAMMI Core. All Deck App commands are compatible with Deck Hopper.  
You can also swipe up or down to navigate between decks when Hopper button in Edit mode on mobile, or press Up or Down Arrow keys on Desktop.
        `,
        reportBug: `
You can report a bug directly in the app by going to Menu - Help Tab - Report a Bug.   
For account-related issues, please use the [Contact Us](https://christinak.ca/deckhopper/contact) form.
        `,
        minAndroid: `
Deck Hopper for Android requires Android 5.1 or higher, with an updated Android System WebView or Chrome browser installed.
        `,
        iosWear: `
Deck Hopper for iOS is planned but requires a Mac and iOS device for development and testing. If interested, consider [donating](https://www.paypal.com/donate/?hosted_button_id=3YWXYQE3HKWHQ) to help cover Apple Developer Fees and the cost of an iOS device.  
WebView, the core component of Deck Hopper, is not supported on Wear OS, so a Wear OS version is not planned.
        `,
        desktopPlatforms: `
Deck Hopper for Desktop is available for Windows, macOS and Linux.
        `,
        differenceApps: `
**LioranBoard 2 Stream Deck** was the original app for LioranBoard 2, developed by Lioran. It has been officially deprecated and is no longer supported by SAMMI. As of SAMMI 2023.3.1, users must switch to SAMMI Panel or Deck Hopper.  
<br>
**SAMMI Panel** is the official app for SAMMI Core, developed by the SAMMI team. It is still available but no longer maintained or updated.  
<br>
**Deck Hopper** is a standalone app developed solely by Christina Kral, SAMMI founder and extension developer. It's designed to be efficient and fix many issues present in the SAMMI Panel. The Pro version includes features like deck status changes, button stopping, SAMMI resets, deck shortcuts, and more planned for future updates.
        `,
        remoteConnection: `
Remote access is planned for the Deck Hopper Pro version, but not yet implemented.   
In the meantime, you can follow these [instructions for using Ngrok for connecting to SAMMI remotely](https://sammi.solutions/docs/faq/general#remoteconnection).
        `,
        rabbits: `
I chose rabbits as my brand because they are my beloved pets.  
<br>
Each time you open Deck Hopper, one of my rabbits will greet you on the Connection Screen:  
- **Archie** is our 9-year-old white bunny, curious and adventurous.  
- **Skye** is our 4-year-old grey rabbit, adopted from a shelter, full of energy.  
- **Hazel**, a chocolate-colored bunny, holds a special place in our hearts as we recently lost her.
- **Rudy**, a lionhead rabbit, was my first bunny that made me fall in love with rabbits.
        `,
        notFreeAnymore: `
I created Deck Hopper as my own personal project to fix some unresolved issues in SAMMI Panel and to introduce new exciting features.  
<br>
Originally, the agreement was to keep Deck Hopper free as long as it carried the "recommended" tag in SAMMI Core and was included in all new SAMMI downloads. Although the tag was eventually removed, breaking the agreement, I still decided to keep Deck Hopper free.  
<br>
Recently, a new policy banning all AI-generated art in official SAMMI spaces was implemented, forcing me to spend countless hours updating every instance where I used AI-generated graphics — in website posts, all Discord messages containing screenshots or trailers, as well as commissioning new artwork for Deck Hopper. Unfortunately, this has severely cut into my development time, causing me to stop working on some other projects.   
<br>
Additionally, supporting iOS and macOS requires a $100 annual fee for Apple's developer program. These combined factors have made it unsustainable to continue offering Deck Hopper for free.  
<br>
Even though I may not agree with every change, I always fully comply with any policies SAMMI Devs implement. I appreciate the early warning about the new AI policy - it allowed me to rework everything before the deadline and express my concerns about its implications.  
<br>
Fortunately, when I previously mentioned the possibility of making Deck Hopper a paid product, SAMMI Devs reassured me that free alternatives like SAMMI Panel would remain available and actively maintained, emphasizing that Deck Hopper was meant as an optional luxury rather than an essential part of SAMMI.  
<br>
Given all this, I must offset Deck Hopper's rising costs and will discontinue the free version. Deck Hopper Basic is now $4.99 (currently discounted to $2.99) and Pro is $7.99 (currently discounted to $5.99)—both are lifetime licenses for all your devices and currently supported platforms. I've invested hundreds of hours into Deck Hopper, so I believe this pricing is very fair.
<br>  
Thank you for your support!
        `
    };

  /* ---------------- render ---------------- */
  return (
    <div className="flex flex-col items-center bg-dark-blue text-gray-300 p-4 w-full mx-auto pb-10">
      <h1 className="text-3xl md:text-4xl text-center mt-4 mb-4">FAQ</h1>
      <p className="text-lg md:text-xl text-center text-gray-400 mb-6">
        Find answers to common Deck Hopper questions.
      </p>
      <hr className="border-t border-gray-600 w-2/3 mb-8" />

      <div className="flex flex-col w-full max-w-screen-lg">
        <ExpandableSection
          title="Can I use Deck Hopper for free?"
          markdownContent={contents.canUseFree}
          isOpen={openSection === 'canUseFree'}
          onToggle={() => openAndScroll('canUseFree')}
          anchor="canUseFree"
        />

        <ExpandableSection
          title="What's the difference between Deck Hopper Basic and Pro?"
          markdownContent={contents.differencePro}
          isOpen={openSection === 'differencePro'}
          onToggle={() => openAndScroll('differencePro')}
          anchor="differencePro"
        />

        <ExpandableSection
          title="What's the beta program?"
          markdownContent={contents.betaProgram}
          isOpen={openSection === 'betaProgram'}
          onToggle={() => openAndScroll('betaProgram')}
          anchor="betaProgram"
        />

        <ExpandableSection
          title="How can I suggest a feature?"
          markdownContent={contents.suggestFeature}
          isOpen={openSection === 'suggestFeature'}
          onToggle={() => openAndScroll('suggestFeature')}
          anchor="suggestFeature"
        />

        <ExpandableSection
          title="How do I switch decks via SAMMI?"
          markdownContent={contents.switchDeck}
          isOpen={openSection === 'switchDeck'}
          onToggle={() => openAndScroll('switchDeck')}
          anchor="switchDeck"
        />

        <ExpandableSection
          title="How do I report a bug?"
          markdownContent={contents.reportBug}
          isOpen={openSection === 'reportBug'}
          onToggle={() => openAndScroll('reportBug')}
          anchor="reportBug"
        />

        <ExpandableSection
          title="Minimum Android version?"
          markdownContent={contents.minAndroid}
          isOpen={openSection === 'minAndroid'}
          onToggle={() => openAndScroll('minAndroid')}
          anchor="minAndroid"
        />

        <ExpandableSection
          title="Will there be an iOS or Wear OS version?"
          markdownContent={contents.iosWear}
          isOpen={openSection === 'iosWear'}
          onToggle={() => openAndScroll('iosWear')}
          anchor="iosWear"
        />

        <ExpandableSection
          title="Supported desktop platforms?"
          markdownContent={contents.desktopPlatforms}
          isOpen={openSection === 'desktopPlatforms'}
          onToggle={() => openAndScroll('desktopPlatforms')}
          anchor="desktopPlatforms"
        />

        <ExpandableSection
          title="Difference between Deck Hopper, LB2 Stream Deck, SAMMI Panel?"
          markdownContent={contents.differenceApps}
          isOpen={openSection === 'differenceApps'}
          onToggle={() => openAndScroll('differenceApps')}
          anchor="differenceApps"
        />

        <ExpandableSection
          title="Can I connect to SAMMI remotely?"
          markdownContent={contents.remoteConnection}
          isOpen={openSection === 'remoteConnection'}
          onToggle={() => openAndScroll('remoteConnection')}
          anchor="remoteConnection"
        />

        <ExpandableSection
          title="What's with the rabbits?"
          markdownContent={contents.rabbits}
          isOpen={openSection === 'rabbits'}
          onToggle={() => openAndScroll('rabbits')}
          anchor="rabbits"
        />

        <ExpandableSection
          title="Why is Deck Hopper no longer free?"
          markdownContent={contents.notFreeAnymore}
          isOpen={openSection === 'notFreeAnymore'}
          onToggle={() => openAndScroll('notFreeAnymore')}
          anchor="notFreeAnymore"
        />
      </div>
    </div>
  );
};

export default FAQ;
